/**
 * Matching and ordering Mongolian roster names.
 *
 * ⭐ THE FORMAT DECIDES EVERYTHING HERE. Names are written `Б.Одмөнх` -- one
 * initial, a dot, then the given name people actually use and are listed by.
 * So the part that matters is AFTER the dot, and a search or a sort that works
 * on the raw string is working on the initial, which is close to noise: sorting
 * by it groups unrelated people under `Б`, and searching by it matches whoever
 * happens to share a first letter with a surname.
 *
 * Ported from the source app's `lastNm` / `lastCmp` / `nameMatch`.
 */

/**
 * The part of a name people are listed by -- everything after the first dot.
 *
 * Falls back to the whole string when there is no dot, because not every name
 * in the roster is in the initial-dot form (`Амарсайхан`, `Батзориг`) and those
 * are already the part that matters.
 */
export function lastName(name: string): string {
  const s = String(name ?? '')
  const dot = s.indexOf('.')
  const tail = (dot >= 0 ? s.slice(dot + 1) : s).trim()
  return tail || s
}

/**
 * Order two names the way the roster lists them.
 *
 * `localeCompare`, so Cyrillic collates properly rather than by code point.
 * The full string breaks ties, so two people with the same given name keep a
 * stable, predictable order rather than swapping around between renders.
 */
export function compareNames(a: string, b: string): number {
  return lastName(a).localeCompare(lastName(b)) || String(a).localeCompare(String(b))
}

/**
 * Does this name match what has been typed?
 *
 * ⛔ A ONE-LETTER QUERY MATCHES THE GIVEN NAME'S FIRST LETTER, AND NOTHING ELSE.
 * That special case is the whole reason this is not `includes()`. Type `о` into
 * a plain substring search over `Б.Одмөнх` and you match every person whose
 * INITIAL is `О` as well as every person with an `о` anywhere in the middle of
 * their name -- which on this roster is most of it, so the list barely narrows
 * and the first keystroke feels broken. Restricted to "given name starts with",
 * one letter does what a person flicking through an alphabetical list expects.
 *
 * From two characters the query is unambiguous enough to loosen: it matches a
 * given-name prefix, or anywhere in the full string, so `бо` still finds
 * `Б.Одмөнх` and `дмө` still finds it too.
 */
export function nameMatches(name: string, query: string): boolean {
  const q = String(query ?? '').trim().toLowerCase()
  if (!q) return true
  const given = lastName(name).toLowerCase()
  if (q.length === 1) return given.startsWith(q)
  return given.startsWith(q) || String(name).toLowerCase().includes(q)
}

/** How the name column can be ordered. */
type NameSort = 'role' | 'asc' | 'desc'

/**
 * Sort names, with the roster's default being BY ROLE.
 *
 * Role first is the source app's default and the right one: a planner reads the
 * grid looking for a geologist, not for a letter, so grouping the roles keeps
 * the people who could cover each other next to one another. Within a role, and
 * for the two alphabetical orders, `compareNames` decides.
 */
export function sortNames(
  names: readonly string[],
  sort: NameSort,
  roleOf: (name: string) => string,
): string[] {
  const out = [...names]
  if (sort === 'role') {
    out.sort((a, b) => roleOf(a).localeCompare(roleOf(b)) || compareNames(a, b))
  } else {
    out.sort((a, b) => (sort === 'desc' ? -compareNames(a, b) : compareNames(a, b)))
  }
  return out
}
